import { Box, Grid, Typography, Stack, useTheme } from "@mui/material"
import React, { useState } from "react"
import { Col, Row } from "react-bootstrap"
import AppTextField from "components/StyledComponents/AppTextField"
import StyledLabel from "components/StyledComponents/StyledLabel"
import AppDatePicker from "components/AppDatePicker"
// import { capitalize } from "helpers/functions"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import RedCloseIcon from "assets/icons/RedCloseIcon.svg"
import { AddOutlined } from "@mui/icons-material"
import AppButton from "components/StyledComponents/AppButton"
import { capitalize } from "helpers/functions"
import ImageCropper from "components/ImageCropper"
import { useDispatch } from "react-redux"
import { uploadVipImageAction } from "redux/superAdmin/actions"
import { superAdminAction } from "redux/superAdmin/slice/user"

const VipGroupDynamicForm = ({ formik, dynamicFieldsGroup, activeStep }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [uploadFieldName, setUploadFieldName] = useState("")

  const onImageUpload = (image, fieldName) => {
    if (image) {
      setUploadFieldName(fieldName)
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  const handleCroppedImage = (image, formik) => {
    if (uploadFieldName) {
      let data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)
      image.preview = URL.createObjectURL(image)
      formik.setFieldValue(uploadFieldName, image)
      dispatch(uploadVipImageAction({ data, fieldName: uploadFieldName }))
      setUploadFieldName("")
    }
  }

  const getComponentType = (label) => {
    switch (label) {
      case "text":
        return "textField"
      case "image":
      case "images":
        return "imageUpload"
      case "file":
        return "uploadPdf"
      case "time":
        return "timeField"
      case "number_field":
        return "numberField"
      case "date":
        return "datePicker"
      default:
        return "textField"
    }
  }

  const multiItemSections = dynamicFieldsGroup?.filter((s) => {
    const typeMatch = ["image_scroll", "array"].includes(s.field_type?.toLowerCase())
    const hasSchema = Object.keys(s.field_schema || {}).length > 0
    return typeMatch && hasSchema
  })
  const detailSections = dynamicFieldsGroup?.filter((s) => {
    const typeMatch = s.field_type?.toLowerCase() === "details"
    const hasSchema = Object.keys(s.field_schema || {}).length > 0
    return typeMatch && hasSchema
  })

  const renderFields = (schema, sectionKey, itemIndex = null) => {
    return Object.entries(schema || {}).map(([fieldKey, fieldType], fieldIndex) => {
      const componentType = getComponentType(fieldType?.toLowerCase())
      const fieldPath =
        itemIndex !== null ? `${sectionKey}[${itemIndex}].${fieldKey}` : `${sectionKey}.${fieldKey}`

      const fieldValue =
        itemIndex !== null
          ? formik.values?.[sectionKey]?.[itemIndex]?.[fieldKey]
          : formik.values?.[sectionKey]?.[fieldKey]
      const fieldTouched =
        itemIndex !== null
          ? formik.touched?.[sectionKey]?.[itemIndex]?.[fieldKey]
          : formik.touched?.[sectionKey]?.[fieldKey]
      const fieldError =
        itemIndex !== null
          ? formik.errors?.[sectionKey]?.[itemIndex]?.[fieldKey]
          : formik.errors?.[sectionKey]?.[fieldKey]

      switch (componentType) {
        case "textField":
        case "numberField":
          return (
            <Row className="w-100 m-0 mt-4" key={fieldIndex}>
              <Col lg="12">
                <AppTextField
                  id={fieldPath}
                  name={fieldPath}
                  placeholder={`Enter the ${fieldKey}`}
                  type={componentType === "numberField" ? "number" : "text"}
                  fullWidth
                  isUpperCase={true}
                  label={capitalize(fieldKey.replace(/_/g, " "))}
                  value={fieldValue}
                  onChange={formik.handleChange(fieldPath)}
                  onBlur={formik.handleBlur(fieldPath)}
                  error={fieldTouched && Boolean(fieldError)}
                  helperText={fieldTouched && fieldError}
                />
              </Col>
            </Row>
          )
        case "datePicker":
          return (
            <Row className="w-100 m-0 mt-4" key={fieldIndex}>
              <Col lg="12">
                <Grid item sm={12} className="datesContainer">
                  <StyledLabel variant="body1Regular">
                    {capitalize(fieldKey.replace(/_/g, " "))}
                  </StyledLabel>
                  <Box className="dateFieldContainer w-100 mt-2">
                    <AppDatePicker
                      minDate={new Date()}
                      maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                      inputFormat="dd MMM yyyy"
                      value={fieldValue}
                      onChange={(val) => formik.setFieldValue(fieldPath, val)}
                      error={fieldTouched && Boolean(fieldError)}
                      helperText={fieldTouched && fieldError}
                    />
                  </Box>
                </Grid>
              </Col>
            </Row>
          )
        case "imageUpload":
          return (
            <Row className="w-100 m-0 mt-4" key={fieldIndex}>
              <Col lg="12">
                <ImageUploadField
                  label={capitalize(fieldKey.replace(/_/g, " "))}
                  isVariant={true}
                  placeholder={`Upload ${fieldKey.replace(/_/g, " ")}`}
                  value={fieldValue}
                  onChange={(e) => onImageUpload(e, fieldPath)}
                  onDelete={() => {
                    formik.setFieldValue(fieldPath, null)
                    dispatch(superAdminAction.removeVipImageId({ key: fieldPath }))
                  }}
                  error={fieldTouched && Boolean(fieldError)}
                  helperText={fieldError}
                />
              </Col>
            </Row>
          )
        case "timeField":
          return (
            <Row className="w-100 m-0 mt-4" key={fieldIndex}>
              <Col lg="12">
                <AppTextField
                  id={fieldPath}
                  name={fieldPath}
                  type="time"
                  fullWidth
                  label={capitalize(fieldKey.replace(/_/g, " "))}
                  isUpperCase={true}
                  value={fieldValue}
                  onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
                  error={fieldTouched && Boolean(fieldError)}
                  helperText={fieldTouched && fieldError}
                />
              </Col>
            </Row>
          )
        default:
          return null
      }
    })
  }

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <>
          <Row className="w-100 m-0 mt-4">
            <Col lg="12">
              <AppTextField
                id="name"
                name="name"
                fullWidth
                isUpperCase={true}
                label="Name"
                placeholder={`Enter the name`}
                isRequired={true}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Col>
          </Row>
          <Row className="w-100 m-0 mt-4">
            <Col lg="12">
              <AppTextField
                id="description"
                name="description"
                fullWidth
                isRequired={true}
                placeholder={`Enter the description`}
                isUpperCase={true}
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Col>
          </Row>
          <Row className="w-100 m-0 mt-4">
            <Col lg="6">
              <ImageUploadField
                label="Image"
                isVariant={true}
                isRequired={true}
                value={formik.values.image}
                placeholder="Upload image"
                onChange={(e) => onImageUpload(e, "image")}
                onDelete={() => {
                  formik.setFieldValue("image", "")
                  dispatch(superAdminAction.removeVipImageId({ key: "image" }))
                }}
                touched={formik.touched.image}
                error={formik.errors.image}
                helperText={formik.errors.image}
              />
            </Col>
            <Col lg="6">
              <ImageUploadField
                label="Icon"
                isVariant={true}
                isRequired={true}
                value={formik.values.icon}
                placeholder="Upload icon"
                onChange={(e) => onImageUpload(e, "icon")}
                onDelete={() => {
                  formik.setFieldValue("icon", "")
                  dispatch(superAdminAction.removeVipImageId({ key: "icon" }))
                }}
                touched={formik.touched.icon}
                error={formik.errors.icon}
                helperText={formik.errors.icon}
              />
            </Col>
            <Col lg="6" className="mt-4">
              <Grid item sm={12} className="datesContainer">
                <StyledLabel variant="body1Regular">
                  {"Start Date"}
                  <span>*</span>
                </StyledLabel>
                <Box className="dateFieldContainer w-100 mt-2">
                  <AppDatePicker
                    minDate={new Date()}
                    maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                    inputFormat="dd MMM yyyy"
                    value={formik.values.startDate}
                    isRequired={true}
                    onChange={(val) => formik.setFieldValue("startDate", val)}
                    error={formik.errors.startDate}
                    touched={formik.touched.startDate}
                    helperText={formik.errors.startDate}
                  />
                </Box>
              </Grid>
            </Col>
            <Col lg="6" className="mt-4">
              <Grid item sm={12} className="datesContainer">
                <StyledLabel variant="body1Regular">
                  {"End Date"}
                  <span>*</span>
                </StyledLabel>
                <Box className="dateFieldContainer w-100 mt-2">
                  <AppDatePicker
                    minDate={new Date()}
                    maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                    inputFormat="dd MMM yyyy"
                    value={formik.values.endDate}
                    isRequired={true}
                    onChange={(val) => formik.setFieldValue("endDate", val)}
                    error={formik.errors.endDate}
                    touched={formik.touched.endDate}
                    helperText={formik.errors.endDate}
                  />
                </Box>
              </Grid>{" "}
            </Col>
          </Row>
          {/* </Row> */}
          {detailSections?.map((section, secIndex) => (
            <Box key={secIndex} className="mb-4 mt-4">
              {!!section.field_label && (
                <Grid container alignItems="center" className="mb-3">
                  <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
                    {section.field_label}
                  </Typography>
                  <div style={{ flexGrow: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
                </Grid>
              )}
              {renderFields(section.field_schema, section.field_name)}
            </Box>
          ))}
        </>
      )
    } else {
      const section = multiItemSections?.[activeStep - 1]
      if (!section) return null
      const sectionKey = section.field_name
      const items = formik.values?.[sectionKey] || []

      return (
        <Box className="mt-1">
          <Grid container alignItems="center">
            <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
              {section.field_label}
            </Typography>
            <div style={{ flexGrow: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
          </Grid>
          {items?.map((item, id) => (
            <Stack key={id} direction="row" spacing={2} alignItems="start" className="mb-4">
              <Grid
                container
                spacing={2}
                width="95%"
                mt={3}
                mb={3}
                mx="auto"
                sx={{
                  border: `2px solid ${theme.palette.secondary.default}`,
                  borderRadius: "8px",
                  pb: 3,
                  pr: 2
                }}>
                {renderFields(section.field_schema, sectionKey, id)}
              </Grid>
              <Stack width="5%">
                {id !== 0 && (
                  <img
                    className="flex-shrink-0"
                    role="button"
                    src={RedCloseIcon}
                    onClick={() => {
                      const newItems = items.filter((_, i) => i !== id)
                      formik.setFieldValue(sectionKey, newItems)
                    }}
                    alt="Delete"
                    style={{ width: 30, marginTop: "24px", marginLeft: "10px" }}
                  />
                )}
              </Stack>
            </Stack>
          ))}

          <AppButton
            fullWidth
            variant="outlined"
            startIcon={<AddOutlined />}
            onClick={() => {
              formik.setFieldValue(sectionKey, [...items, {}])
            }}
            sx={{ mt: 2 }}>
            Add {section.field_label}
          </AppButton>
        </Box>
      )
    }
  }

  return (
    <Box flexDirection="column">
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={uploadFieldName === "icon" ? 13 / 5 : 1}
        onClose={setCrop}
        openCrop={openCrop}
      />
      {renderStep()}
    </Box>
  )
}

export default VipGroupDynamicForm
